import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ZodType } from 'zod';

import { sendJoiningLink } from '@/api/signUp/signUp.actions.ts';
import inviteIcon from '@/assets/inviteIconGreen.png';
import CustomEntityForm from '@/components/controls/CustomEntityForm';
import { EntityFormFieldSpecification } from '@/components/controls/CustomEntityFormField';
import { ConfirmEmailSchema } from '@/components/zod/types';
import { useAsync } from '@/hooks/global/useAsync';

type CreateJoiningLinkData = {
  email: string;
};

const CreateJoiningLinkForm: FC = () => {
  const navigate = useNavigate();
  const { isLoading } = useAsync();

  const createJoiningLinkFields: EntityFormFieldSpecification[] = [
    {
      name: 'email',
      label: 'Corporate Email',
      fieldType: 'input',
      type: 'email',
      placeholder: 'Enter corporate email to receive the joining link',
      validation: { required: true },
    },
  ];

  const handleSubmit = async (data: CreateJoiningLinkData): Promise<void> => {
    const response = await sendJoiningLink(data);

    if (!response || !response.success) {
      toast.error(response?.error?.message ?? 'Something went wrong');
      return;
    }

    toast.success(response.message);
    navigate('/');
  };

  return (
    <CustomEntityForm<CreateJoiningLinkData>
      schema={ConfirmEmailSchema as ZodType<CreateJoiningLinkData>}
      isLoading={isLoading}
      fields={createJoiningLinkFields}
      initialMode="create"
      onSubmit={handleSubmit}
      onCancel={() => navigate('/')}
      title={
        <div className="flex items-center justify-center md:justify-start gap-2">
          <img
            src={inviteIcon}
            alt="Invite icon"
            className="w-6 h-6 object-contain"
          />
          <span>Invite corporate user</span>
        </div>
      }
      submitButtonText="Send"
      submitButtonDisabled
    />
  );
};

export default CreateJoiningLinkForm;
